// # Analytics Component for Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import './analytics.css';

// # Import analytics icons
import growthIcon from '../../../aneo_media/analytics/growth.png';
import authIcon from '../../../aneo_media/analytics/authentication.png';
import activityIcon from '../../../aneo_media/analytics/activity.png';
import subscriptionsIcon from '../../../aneo_media/analytics/subscriptions.png';
import technicalIcon from '../../../aneo_media/analytics/technical.png';
import globeIcon from '../../../aneo_media/analytics/globe.png';

const Analytics = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Redirect if not admin
  React.useEffect(() => {
    const startTime = performance.now();
    
    console.log('Analytics Dashboard - Admin access check:', {
      operation: 'admin_check',
      isAdmin: !!isAdmin,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });

    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Analytics sections configuration
  const analyticsSections = [
    {
      title: 'Users & Growth Analytics',
      icon: growthIcon,
      path: '/admin/analytics/growth',
      description: 'Track user growth and platform expansion metrics'
    },
    {
      title: 'Activity Analytics',
      icon: activityIcon,
      path: '/admin/analytics/activity',
      description: 'Monitor user engagement and platform activity'
    },
    {
      title: 'Global Distribution',
      icon: globeIcon,
      path: '/admin/analytics/heatmap',
      description: 'View global user distribution and activity'
    },
    {
      title: 'Subscription Analytics',
      icon: subscriptionsIcon,
      path: '/admin/analytics/subscriptions',
      description: 'Monitor subscription trends and metrics'
    },
    {
      title: 'Technical Analytics',
      icon: technicalIcon,
      path: '/admin/analytics/technical',
      description: 'Track platform performance and technical metrics'
    },
    {
      title: 'Authentication Analytics',
      icon: authIcon,
      path: '/admin/analytics/auth',
      description: 'Monitor user authentication and security metrics'
    }
  ];

  return (
    <div className={`altc_admin-page ${isDarkMode ? 'altc_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="altc_admin-content">
        <div className="altc_admin-header">
          <button 
            className="altc_back-button"
            onClick={() => navigate('/admin')}
          >
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
          <h1>Analytics Dashboard</h1>
        </div>

        <div className="altc_analytics-grid">
          {analyticsSections.map((section, index) => (
            <div 
              key={index}
              className="altc_analytics-card"
              onClick={() => navigate(section.path)}
            >
              <div className="altc_analytics-icon">
                <img src={section.icon} alt={section.title} />
              </div>
              <h3>{section.title}</h3>
              <p>{section.description}</p>
            </div>
          ))}
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Analytics;
